// Decoupled from the popup's own open/close API — only decides *when* to call `.open()`.
// Defined before ExitIntentPopup: customElements.define() upgrades the already-in-DOM
// <exit-intent-popup> element synchronously, so connectedCallback() (which calls
// ExitIntentTrigger.init()) can run before later statements in this file otherwise would.
const ExitIntentTrigger = {
  STORAGE_KEY: 'wip-exit-intent-shown',
  ARM_DELAY_MS: 4000,
  // Set by the A/B test tool's variation JS on <body> for visitors bucketed into
  // the "show popup" variant. Auto-triggering only arms when this class is present.
  AB_VARIANT_CLASS: 'wip-exit-intent-ab-variant',
  _initialized: false,

  init(popup) {
    if (this._initialized) return;
    this._initialized = true;
    this._popup = popup;

    window.__openExitIntentPopup = () => popup.open('state1');
    window.__resetExitIntentPopup = () => sessionStorage.removeItem(this.STORAGE_KEY);

    const params = new URLSearchParams(window.location.search);
    if (params.get('exit-intent') === '1') {
      popup.open('state1');
      return;
    }

    if (sessionStorage.getItem(this.STORAGE_KEY)) return;

    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const delaySeconds = Number(popup.dataset.mobileDelay) || 35;

    this._armTimeout = setTimeout(() => {
      if (isTouch) {
        this._mobileTimer = setTimeout(() => this._fire(), delaySeconds * 1000);
      } else {
        this._onMouseLeave = (event) => {
          if (event.clientY <= 0 && event.relatedTarget === null) this._fire();
        };
        document.addEventListener('mouseleave', this._onMouseLeave);
      }
    }, this.ARM_DELAY_MS);
  },

  _fire() {
    // Checked here rather than at arm time: the variant class is added by the A/B
    // tool's own script (commonly on DOMContentLoaded, which can run after this
    // deferred script), so the class may not exist yet when listeners are armed.
    if (!document.body.classList.contains(this.AB_VARIANT_CLASS)) return;
    if (sessionStorage.getItem(this.STORAGE_KEY)) return;
    sessionStorage.setItem(this.STORAGE_KEY, '1');
    this._teardown();
    this._popup.open('state1');
  },

  _teardown() {
    clearTimeout(this._armTimeout);
    clearTimeout(this._mobileTimer);
    if (this._onMouseLeave) document.removeEventListener('mouseleave', this._onMouseLeave);
  },
};

class ExitIntentPopup extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
  }

  connectedCallback() {
    this._overlay = this.querySelector('.exit-intent-popup__overlay');
    this._card = this.querySelector('.exit-intent-popup__card');
    this._closeButton = this.querySelector('.exit-intent-popup__close');
    this._tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this._form = this.querySelector('.exit-intent-popup__form');
    this._emailInput = this.querySelector('input[name="email"]');
    this._submitButton = this.querySelector('.exit-intent-popup__submit');
    this._errorEl = this.querySelector('.exit-intent-popup__error');
    this._panels = {
      state1: this.querySelector('[data-panel="state1"]'),
      state2: this.querySelector('[data-panel="state2"]'),
    };
    this._scratchCard = this.querySelector('scratch-reveal-card');

    this._companyId = this.dataset.companyId;
    this._listId = this.dataset.listId;
    this._discountCode = this.dataset.discountCode;

    if (this._overlay) this._overlay.addEventListener('click', this.close.bind(this));
    if (this._closeButton) this._closeButton.addEventListener('click', this.close.bind(this));
    this._tabs.forEach((tab) => tab.addEventListener('click', () => this._onTabClick(tab.dataset.tab)));
    if (this._form) this._form.addEventListener('submit', this._onSubmit.bind(this));
    this.addEventListener('scratch-revealed', this._onScratchRevealed.bind(this));

    ExitIntentTrigger.init(this);
  }

  open(initialPanel = 'state1') {
    this.activeElement = document.activeElement;
    this.removeAttribute('hidden');
    this._setPanel(initialPanel);
    document.body.classList.add('overflow-hidden');
    this.dispatchEvent(new CustomEvent('exit-intent-popup-open'));
  }

  close() {
    this.setAttribute('hidden', '');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.dispatchEvent(new CustomEvent('exit-intent-popup-close'));
  }

  _onTabClick(tabName) {
    // The tab bar is shared chrome shown on both panels. On state2, "Scratch & Reveal"
    // just reflects where you already are (no-op); "Enter Email" backs out to state1.
    if (this.dataset.activePanel === 'state2') {
      if (tabName === 'email') {
        this._setPanel('state1');
        this._setActiveTab('email');
      }
      return;
    }
    // From state1, "Scratch & Reveal" is disabled until the email step has been
    // completed once, so reaching this click at all means the code is ready.
    if (tabName === 'scratch') {
      this._setPanel('state2');
    }
    this._setActiveTab(tabName);
  }

  _setActiveTab(tabName) {
    this._tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === tabName;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });
    this.dataset.activeTab = tabName;
  }

  _setPanel(panelName) {
    Object.entries(this._panels).forEach(([name, el]) => {
      if (!el) return;
      el.hidden = name !== panelName;
    });
    this.dataset.activePanel = panelName;

    // Re-trap on every switch: trapFocus captures its focusable-element list at call
    // time, so it goes stale once the previously-visible panel's fields become hidden.
    if (!this.hasAttribute('hidden')) {
      const focusTarget = panelName === 'state1' ? this._emailInput : this._card;
      trapFocus(this._card, focusTarget || this._card);
    }
  }

  _setError(message) {
    if (!this._errorEl) return;
    if (message) {
      this._errorEl.textContent = message;
      this._errorEl.hidden = false;
    } else {
      this._errorEl.hidden = true;
      this._errorEl.textContent = '';
    }
  }

  _setLoading(state) {
    if (!this._submitButton) return;
    this._submitButton.classList.toggle('loading', state);
    this._submitButton.disabled = state;
  }

  async _onSubmit(event) {
    event.preventDefault();
    this._setError('');

    const email = (this._emailInput.value || '').trim();
    if (!email || !this._emailInput.checkValidity()) {
      this._emailInput.classList.add('error');
      this._setError(this.dataset.errorInvalidEmail || 'Please enter a valid email address.');
      return;
    }
    this._emailInput.classList.remove('error');

    if (!this._companyId || !this._listId) {
      console.error('[exit-intent-popup] Missing Klaviyo company id / list id theme settings');
      this._setError(this.dataset.errorGeneric || 'Something went wrong — please try again.');
      return;
    }

    this._setLoading(true);

    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${this._companyId}`, {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          revision: '2025-07-15',
          'content-type': 'application/vnd.api+json',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              custom_source: 'Exit Intent Popup',
            },
            relationships: {
              list: { data: { type: 'list', id: this._listId } },
            },
          },
        }),
      });

      if (!response.ok) throw new Error(`Klaviyo API error: ${response.status}`);

      this.dispatchEvent(new CustomEvent('exit-intent-popup-subscribed', { detail: { email } }));
      this.querySelectorAll('[data-tab="scratch"]').forEach((tab) => {
        tab.disabled = false;
      });
      this._setPanel('state2');
      this._setActiveTab('scratch');

      requestAnimationFrame(() => {
        if (this._scratchCard) this._scratchCard.init(this._discountCode);
      });
    } catch (err) {
      console.error('[exit-intent-popup] Klaviyo subscribe failed', err);
      this._setError(this.dataset.errorGeneric || 'Something went wrong — please try again.');
    } finally {
      this._setLoading(false);
    }
  }

  _onScratchRevealed(event) {
    this._applyDiscount(event.detail?.code || this._discountCode);
    this.dispatchEvent(new CustomEvent('exit-intent-popup-revealed'));
  }

  // Shopify's /discount/<code> route silently applies a code to the cart session
  // and issues a redirect — load it in a hidden iframe so it never navigates the
  // visible page. This is what makes "Applied at checkout automatically" true;
  // Copy is just a convenience for referencing the code, not what applies it.
  _applyDiscount(code) {
    if (!code) return;
    const iframe = document.createElement('iframe');
    iframe.hidden = true;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/')}`;
    iframe.addEventListener('load', () => iframe.remove());
    setTimeout(() => iframe.remove(), 5000);
    document.body.appendChild(iframe);
  }
}

customElements.define('exit-intent-popup', ExitIntentPopup);
